import { FC, ReactNode } from "react";
import { ICancellationPreview } from "../../../interfaces/reservations/Cancellation.types";
import { cx } from "../../../utils/classnames";
import { eur } from "../../../utils/eur";
import { cancellationRefusalSentence } from "../../../utils/reservations/cancellationText";
import InfoTip from "./InfoTip";

const Note: FC<{ tone?: "info" | "warn"; children: ReactNode }> = ({ tone = "info", children }) => (
  <div
    className={cx([
      "rounded-md border px-3 py-2 text-xs",
      tone === "warn"
        ? "border-orange-300 bg-orange-100/30 text-orange-900"
        : "bg-gray-50 border-gray-200 text-gray-600"
    ])}
  >
    {children}
  </div>
);

// A label, with the sentence behind it in a tooltip.
const Flag: FC<{ label: string; tip?: string }> = ({ label, tip }) => (
  <span className="flex items-center gap-1">
    {label}
    {tip && <InfoTip text={tip} />}
  </span>
);

const REFUSAL_LABELS: Record<string, string> = {
  "dispute.open": "Kontestim i hapur",
  "quote.stale": "Rezervimi ndryshoi ndërkohë"
};

const Row: FC<{ label: string; value: number; strong?: boolean }> = ({ label, value, strong }) => (
  <div className={cx(["flex justify-between gap-4", strong && "font-semibold text-black"])}>
    <span>{label}</span>
    <span className="tabular-nums">{eur(value)}</span>
  </div>
);

// Shows the server's figures exactly as sent: nothing here adds, splits or rounds money.
const PreviewCard: FC<{
  preview: ICancellationPreview | null;
  loading: boolean;
  error: string | null;
  staleNotice: string | null;
  onRetry: () => void;
  /** The admin chose "Pa rimbursim". */
  noRefund?: boolean;
  wholeBooking?: boolean;
}> = ({ preview, loading, error, staleNotice, onRetry, noRefund = false, wholeBooking = false }) => {
  if (error)
    return (
      <div className="rounded-md border border-red-300 bg-red-100/50 px-3 py-2 text-xs text-red-700">
        {error}{" "}
        <button type="button" className="underline" onClick={onRetry}>
          Provo përsëri
        </button>
      </div>
    );
  if (!preview)
    return loading ? (
      <div aria-busy className="h-24 rounded-md bg-gray-100 animate-pulse" />
    ) : (
      <p className="text-xs text-gray-500">Asnjë biletë e zgjedhur</p>
    );

  const nothing = preview.toCard <= 0 && preview.toWallet <= 0;
  return (
    <div className={cx(["flex flex-col gap-3", loading && "opacity-50"])} aria-busy={loading}>
      {staleNotice && (
        <Note tone="warn">
          <Flag label={REFUSAL_LABELS["quote.stale"]} tip={staleNotice} />
        </Note>
      )}
      <div className="rounded-md border border-gray-200 px-3 py-2 flex flex-col gap-1 text-sm">
        {preview.items.map((item, index) => (
          <Row key={`${item.product}-${item.ticketId ?? "b"}-${index}`} label={item.label}
            value={item.value} />
        ))}
        <Row label="Vlera që kthehet" value={preview.value} strong />
      </div>
      {!nothing && (
        <div className="flex flex-col gap-1 text-sm">
          {preview.toCard > 0 && <Row label="Në kartelë" value={preview.toCard} strong />}
          {preview.toWallet > 0 && <Row label="Në Gjirafa Money" value={preview.toWallet} strong />}
          {preview.toWalletPromotional > 0 && (
            <div className="pl-3 text-xs text-gray-500">
              <Row label="prej tyre Gjirafa Money promocionale" value={preview.toWalletPromotional} />
            </div>
          )}
        </div>
      )}
      {(preview.operatorDeduction > 0 || (preview.collapsesBooking && !wholeBooking)) && (
        <div className="flex flex-col gap-1 text-xs text-gray-500">
          {preview.operatorDeduction > 0 && (
            <div className="flex justify-between gap-4">
              <Flag label="Zbritje nga fatura e operatorit"
                tip="Vetëm për dijeni; nuk ndryshon shumën që i kthehet klientit." />
              <span className="tabular-nums">−{eur(preview.operatorDeduction)}</span>
            </div>
          )}
          {preview.collapsesBooking && !wholeBooking && (
            <Flag label="Anulohet i gjithë rezervimi"
              tip="Nuk mbetet asnjë biletë aktive, prandaj rezervimi shënohet si i anuluar." />
          )}
        </div>
      )}
      {noRefund && !nothing && !loading && (
        <Note tone="warn">
          <Flag label="Anulimi nuk mund të kryhet pa rimbursim"
            tip="Ky anulim do t'i kthente ende para klientit. Nëse përsëritet, njoftoni ekipin teknik." />
        </Note>
      )}
      {preview.refusals.map((refusal) => (
        <Note key={refusal.code} tone="warn">
          {REFUSAL_LABELS[refusal.code] ? (
            <Flag label={REFUSAL_LABELS[refusal.code]} tip={cancellationRefusalSentence(refusal)} />
          ) : (
            cancellationRefusalSentence(refusal)
          )}
        </Note>
      ))}
    </div>
  );
};

export default PreviewCard;
